class KlaviyoSubscriptionForm extends HTMLElement {
  constructor() {
    super();

    this.onFormSubmitListener = this.onFormSubmit.bind(this);
  }

  get selectors() {
    return {
      fields: {
        submit: "[data-field-type='submit']",
        email: "[data-field-type='email']",
        phone: "[data-field-type='phone']",
        customProfileProperty: "[data-field-type='custom-profile-property']"
      },
      message: "[data-klaviyo-message]",
      currentPhoneCountryCode: "[data-back-in-stock-country-code]",
      phoneCountryCode: "[data-country-code]"
    }
  }

  get attributes() {
    return {
      listId: "list-id",
      customSource: "custom-source",
      channel: "channel",
      countryCode: "data-country-code",
      profilePropertyName: "data-profile-property"
    }
  }

  get modifiers() {
    return {
      hidden: "hidden",
      message: {
        error: "product-message--error",
        success: "product-message--success"
      }
    }
  }

  get channels() {
    return {
      email: "EMAIL",
      sms: "SMS"
    }
  }

  get messageTypes() {
    return {
      success: "success",
      error: "error"
    }
  }

  get publicApiKey() {
    return theme.klaviyo_public_api_key;
  }

  get listId() {
    return this.getAttribute(this.attributes.listId);
  }

  get customSource() {
    return this.getAttribute(this.attributes.customSource);
  }

  get channel() {
    return this.getAttribute(this.attributes.channel);
  }

  connectedCallback() {
    const submitElement = this.querySelector(this.selectors.fields.submit);

    if (!submitElement) {
      return;
    }

    submitElement.addEventListener("click", this.onFormSubmitListener);
  }

  async onFormSubmit(event) {
    event.preventDefault();

    const {
      email,
      customProfileProperties
    } = this.extractProfileData();

    const profileAttributes = {
      email: email,
      ...(customProfileProperties && {
        properties: {
          ...customProfileProperties
        }
      })
    };

    const response = await this.createClientSubscription(profileAttributes, this.customSource, this.listId);

    if (!response.ok) {
      const data = await response.json();
      console.error(data);

      this.onSubscriptionError(data?.errors[0]?.detail);
      return;
    }

    this.onSubscriptionSuccess();
  }

  async createClientSubscription(profileAttributes, customSource, listId) {
    const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.publicApiKey}`;

    if (!profileAttributes.subscriptions) {
      profileAttributes.subscriptions = {};
    }

    if (profileAttributes.email) {
      profileAttributes.subscriptions.email = {
        marketing: {
          consent: "SUBSCRIBED"
        }
      };
    }

    if (profileAttributes.phone_number) {
      profileAttributes.subscriptions.sms = {
        marketing: {
          consent: "SUBSCRIBED"
        }
      };
    }

    const options = {
      method: "POST",
      headers: {
        revision: "2025-01-15",
        "content-type": "application/vnd.api+json"
      },
      body: JSON.stringify({
        data: {
          type: "subscription",
          attributes: {
            ...(customSource && {
              custom_source: customSource
            }),
            profile: {
              data: {
                type: "profile",
                attributes: {
                  ...profileAttributes
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: "list",
                id: listId
              }
            }
          }
        }
      })
    };

    return await fetch(url, options);
  }

  onSubscriptionError(message = theme.strings.klaviyo.errors.generic) {
    this.renderMessage(this.messageTypes.error, message);
  }

  onSubscriptionSuccess() {
    const message = this.channel === this.channels.sms
      ? theme.strings.klaviyo.success.phone
      : theme.strings.klaviyo.success.generic;

    this.renderMessage(this.messageTypes.success, message);
  }

  extractProfileData() {
    const email = this.querySelector(this.selectors.fields.email)?.value ?? "";

    let phoneNumber = this.querySelector(this.selectors.fields.phone)?.value ?? "";
    const currentPhoneCountryCode = this.querySelector(this.selectors.currentPhoneCountryCode);

    if (phoneNumber && currentPhoneCountryCode) {
      const phoneCountryCode = currentPhoneCountryCode.querySelector(this.selectors.phoneCountryCode).getAttribute(this.attributes.countryCode);
      phoneNumber = `${phoneCountryCode}${phoneNumber}`;
    }

    const customProfileProperties = this.extractCustomProfileProperties();

    return {
      email,
      phoneNumber,
      customProfileProperties
    };
  }

  extractCustomProfileProperties() {
    const customProfilePropertyInputs = [...this.querySelectorAll(this.selectors.fields.customProfileProperty)];

    if (!customProfilePropertyInputs.length) {
      return;
    }

    const customProfileProperties = {};

    const groupedInputs = customProfilePropertyInputs.reduce((groups, input) => {
      const inputName = input.getAttribute(this.attributes.profilePropertyName);

      if (!groups[inputName]) {
        groups[inputName] = [];
      }

      groups[inputName].push(input);

      return groups;
    }, {});

    for (const groupName in groupedInputs) {
      const groupInputs = groupedInputs[groupName];

      const selectedInputs = groupInputs.filter(input => (input.type !== "checkbox" && input.checked) || (input.type === "checkbox" && input.checked));

      const values = selectedInputs.map(input => (input.type === "checkbox" ? input.value : input.value)).join(", ");

      customProfileProperties[groupName] = values;
    }

    return customProfileProperties;
  }

  renderMessage(type = null, message = "") {
    const messageElement = this.querySelector(this.selectors.message);

    if (!messageElement) {
      return;
    }

    // Force text mutation so identical consecutive states re-announce
    messageElement.innerHTML = "";
    requestAnimationFrame(() => {
      messageElement.innerHTML = message;
    });

    switch (type) {
      case this.messageTypes.success: {
        messageElement.classList.remove(this.modifiers.message.error);
        messageElement.classList.add(this.modifiers.message.success);

        messageElement.classList.remove(this.modifiers.hidden);

        break;
      }

      case this.messageTypes.error: {
        messageElement.classList.remove(this.modifiers.message.success);
        messageElement.classList.add(this.modifiers.message.error);

        messageElement.classList.remove(this.modifiers.hidden);

        break;
      }

      default: {
        messageElement.classList.add(this.modifiers.hidden);
      }
    }
  }
}

if (!customElements.get("klaviyo-subscription-form")) {
  customElements.define("klaviyo-subscription-form", KlaviyoSubscriptionForm);
}

class KlaviyoBackInStockSubscriptionForm extends KlaviyoSubscriptionForm {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.marketingCheckbox = this.querySelector(this.selectors.marketingCheckbox);
  }

  get selectors() {
    return {
      ...super.selectors,
      marketingCheckbox: "[data-back-in-stock-marketing-checkbox]"
    }
  }

  get attributes() {
    return {
      ...super.attributes,
      variantId: "variant-id"
    }
  }

  get variantId() {
    return this.getAttribute(this.attributes.variantId);
  }

  async onFormSubmit(event) {
    event.preventDefault();

    const {
      email,
      phoneNumber
    } = this.extractProfileData();

    const profileAttributes = {};

    if (this.channel === this.channels.email) {
      profileAttributes.email = email;
    }

    if (this.channel === this.channels.sms) {
      profileAttributes.phone_number = phoneNumber;
    }

    const response = await this.createClientBackInStockSubscription(profileAttributes, this.channel, this.variantId);

    if (!response.ok) {
      const data = await response.json();
      console.error(data);

      this.onSubscriptionError(data?.errors[0]?.detail);
      return;
    }

    if (this.marketingCheckbox) {
      await this.subscribeToBackInStockMarketingList(profileAttributes);
    }

    this.onSubscriptionSuccess();
  }

  async createClientBackInStockSubscription(profileAttributes, channel, variantId) {
    const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.publicApiKey}`;

    const options = {
      method: "POST",
      headers: {
        accept: "application/vnd.api+json",
        revision: "2025-01-15",
        "content-type": "application/vnd.api+json"
      },
      body: JSON.stringify({
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            channels: [channel],
            profile: {
              data: {
                type: "profile",
                attributes: {
                  ...profileAttributes
                }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      })
    };

    return await fetch(url, options);
  }

  async subscribeToBackInStockMarketingList(profileAttributes) {
    if (!this.marketingCheckbox.checked) {
      return;
    }

    return await this.createClientSubscription(profileAttributes, this.customSource, this.listId);
  }
}

if (!customElements.get("klaviyo-back-in-stock-form")) {
  customElements.define("klaviyo-back-in-stock-form", KlaviyoBackInStockSubscriptionForm);
}

class KlaviyoCustomTrigger extends HTMLElement {
  constructor() {
    super();
  }

  get formId() {
    return this.getAttribute("form-id");
  }

  connectedCallback() {
    this.addEventListener("click", this.onClickListener.bind(this));
    this.addEventListener("keydown", this.onKeydownListener.bind(this));
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClickListener.bind(this));
    this.removeEventListener("keydown", this.onKeydownListener.bind(this));
  }

  openKlaviyoForm() {
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push([
      "openForm",
      this.formId.toString()
    ]);
  }

  onClickListener() {
    this.openKlaviyoForm();
  }

  onKeydownListener(event) {
    if (event.key === "Enter") {
      this.openKlaviyoForm();
    }
  }
}

if (!customElements.get("klaviyo-custom-trigger")) {
  customElements.define("klaviyo-custom-trigger", KlaviyoCustomTrigger);
}
